<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, type Component } from 'vue';
import { AnswerResultType } from '../../../../../types/assessment';
import { useQuizStore, useUserStore } from '../../../../../stores';
import { UserRole } from '../../../../../types/user';
import { useI18n } from 'petite-vue-i18n';
import { TEXT } from '../../../../../configs/text';
import { utils } from '../../../../../utils';

import QuizMutipleChoice from '../patterns/QuizMutipleChoice.vue'
import QuizFillinBlank from '../patterns/QuizFillInBlank.vue'
import ButtonV2 from '../../../../General/ButtonV2.vue';
import QuestionNav from '../../../../Assessment/QuestionNav.vue';
import { audioManager } from '../../../../../utils/audioManager';
import quizReview from '../../../../../assets/audios/quiz_review.mp3'
import { QuizAudioKey } from '../../../../../types/quiz';
import Slide from '../../../../Animation/Slide.vue';
import Flip from '../../../../Animation/Flip.vue';
import { ClassStudentInfo } from '../../../../../types/class';

import pageFlip from "../../../../../assets/audios/page_flip.wav";

interface IComponents {
    [key: string]: Component
}
const components: IComponents = {
    'mca': QuizMutipleChoice,
    'sca': QuizMutipleChoice,
    'gap': QuizFillinBlank
}
const { t } = useI18n()
const emit = defineEmits(['back']);
const quizStore = useQuizStore()
const userStore = useUserStore()
const showSubmission = ref<boolean>(false)
const currentQuestionIndex = ref<number>(0)
const slideDirection = ref<'left' | 'right'>('left')

const currentQuestion = computed(() => {
    return quizStore.quiz?.questions[currentQuestionIndex.value]
})

const studentAnswers = computed<ClassStudentInfo[]>(() => {
    if (userStore.userRole === UserRole.student) return [];
    return quizStore.leaderBoard.filter((student) => {
        return student.quizQuestion?.find((question) => question.id === currentQuestion.value.id)?.answer
    }).sort((a, b) => getStudentAnswerTime(a) - getStudentAnswerTime(b))
})

// enum iteration
// const answerResultTypes = Object.values(AnswerResultType)
const answerResultTypes = Object.values(AnswerResultType).filter((type) => type !== AnswerResultType.ALMOST_CORRECT);

// computed property to categorize answers
const categorizedAnswers = computed(() => {
    const correct = studentAnswers.value?.reduce((total, student) => {
        const studentCurrentQuestionAnswer = getStudentCurentQuestion(student)
        return total + (studentCurrentQuestionAnswer?.answer.every(answer => answer.isCorrect === 1) && studentCurrentQuestionAnswer?.answer.length === studentCurrentQuestionAnswer?.correctAnswers.length ? 1 : 0);
    }, 0);

    const incorrect = studentAnswers.value.length - correct;

    return {
        [AnswerResultType.CORRECT]: correct,
        // [AnswerResultType.INCORRECT]: incorrect,
        [AnswerResultType.INCORRECT]: incorrect,
        // [AnswerResultType.ALMOST_CORRECT]: studentAnswers.value.length - (corect + incorrect),
    };
});

function changeQuestion(index: number) {
    slideDirection.value = currentQuestionIndex.value < index ? 'left' : 'right'
    currentQuestionIndex.value = index
}
function getStudentCurentQuestion(student: ClassStudentInfo) {
    return student.quizQuestion?.find((question) => question.id === currentQuestion.value.id)
}

function getStudentAnswerTime(student: ClassStudentInfo) {
    const studentCurrentQuestionAnswer = getStudentCurentQuestion(student)
    return (studentCurrentQuestionAnswer?.endTime ?? 0) - (studentCurrentQuestionAnswer?.startTime ?? 0)
}

function evaluateAnswers(student: ClassStudentInfo | undefined) {
    if (!student) return { status: null, className: '' }
    const studentCurrentQuestionAnswer = getStudentCurentQuestion(student)
    if (!studentCurrentQuestionAnswer?.answer) return { status: null, className: '' };
    const correct = studentCurrentQuestionAnswer?.answer.every(answer => answer.isCorrect === 1) && studentCurrentQuestionAnswer?.answer.length === studentCurrentQuestionAnswer?.correctAnswers.length;
    // const allIncorrect = answers.every(answer => answer.isCorrect === 0);
    if (correct) return { status: t(TEXT.corrected), className: 'text-lime-500 dark:text-lime-300' };
    return { status: t(TEXT.incorrected), className: 'text-red-600 dark:text-red-400' };
    // return { status: t(TEXT.almost_corrected), className: 'text-yellow-500 dark:text-yellow-300' };
}

function answerRate(type: AnswerResultType) {
    const totalAnswers = studentAnswers.value.length;
    if (totalAnswers === 0) return 0;

    const count = (categorizedAnswers.value as Record<AnswerResultType, number>)[type];
    return ((count / totalAnswers) * 100).toFixed(2); // Return percentage as a string with 2 decimal places
}


onMounted(() => {
    audioManager.stop(QuizAudioKey.SFX)
    audioManager.play(quizReview, QuizAudioKey.SFX)
})

onBeforeUnmount(() => {
    audioManager.stop(QuizAudioKey.SFX)
})

</script>
<template>
    <div class="relative flex flex-col justify-end items-center h-full w-full overflow-hidden"
        v-if="currentQuestion?.id">

        <div
            class="w-full bg-cyan-500 dark:bg-purple-800 text-center font-bold text-lg text-white p-2 rounded-b-2xl first-letter:uppercase">
            {{ t(showSubmission ? TEXT.submission : TEXT.correct_answer) }}
        </div>

        <Flip :direction="'vertical-right'">
            <div class="flex flex-col w-full h-full gap-5 overflow-hidden" v-if="!showSubmission">
                <Slide :direction="slideDirection">
                    <component :is="components[currentQuestion.questionType as keyof typeof components]"
                        :question="currentQuestion" :showAnswer="true" :showSubmission="true" :key="currentQuestion.id"
                        disabled></component>
                </Slide>
            </div>
            <div class="w-full p-3 grow flex flex-col justify-start items-center gap-3 overflow-hidden" v-else>
                <Slide :direction="slideDirection">
                    <div class="w-9/12 max-w-5xl bg-white dark:bg-slate-800 rounded-lg h-full overflow-auto px-5 text-xl"
                        :key="currentQuestion.id">
                        <div class="grid grid-flow-col sticky top-0 bg-white dark:bg-slate-800 font-bold mt-3">
                            <div v-for="type in answerResultTypes" :key="type" class="flex justify-center p-3"
                                :class="{ 'text-lime-500 dark:text-lime-300': type === AnswerResultType.CORRECT, 'text-red-600 dark:text-red-400': type === AnswerResultType.INCORRECT }">
                                <div class="flex items-center gap-3">
                                    <div class="flex items-center gap-2">
                                        <svg class="size-4" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="50" cy="50" r="50" fill="currentColor" />
                                        </svg>
                                        <span>{{ categorizedAnswers[type as keyof typeof categorizedAnswers]
                                        }}</span>
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <svg width="22" height="17" viewBox="0 0 22 17" fill="none"
                                            xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M0 16.5V13.7C0 13.1334 0.146 12.6127 0.438 12.138C0.73 11.6634 1.11733 11.3007 1.6 11.05C2.63333 10.5334 3.68333 10.146 4.75 9.88803C5.81667 9.63002 6.9 9.50069 8 9.50003C9.1 9.49936 10.1833 9.62869 11.25 9.88803C12.3167 10.1474 13.3667 10.5347 14.4 11.05C14.8833 11.3 15.271 11.6627 15.563 12.138C15.855 12.6134 16.0007 13.134 16 13.7V16.5H0ZM18 16.5V13.5C18 12.7667 17.796 12.0624 17.388 11.387C16.98 10.7117 16.4007 10.1327 15.65 9.65003C16.5 9.75003 17.3 9.92103 18.05 10.163C18.8 10.405 19.5 10.7007 20.15 11.05C20.75 11.3834 21.2083 11.754 21.525 12.162C21.8417 12.57 22 13.016 22 13.5V16.5H18ZM8 8.50003C6.9 8.50003 5.95833 8.10836 5.175 7.32503C4.39167 6.54169 4 5.60003 4 4.50003C4 3.40003 4.39167 2.45836 5.175 1.67503C5.95833 0.891692 6.9 0.500026 8 0.500026C9.1 0.500026 10.0417 0.891692 10.825 1.67503C11.6083 2.45836 12 3.40003 12 4.50003C12 5.60003 11.6083 6.54169 10.825 7.32503C10.0417 8.10836 9.1 8.50003 8 8.50003ZM18 4.50003C18 5.60003 17.6083 6.54169 16.825 7.32503C16.0417 8.10836 15.1 8.50003 14 8.50003C13.8167 8.50003 13.5833 8.47936 13.3 8.43803C13.0167 8.39669 12.7833 8.35069 12.6 8.30003C13.05 7.76669 13.396 7.17503 13.638 6.52503C13.88 5.87503 14.0007 5.20003 14 4.50003C13.9993 3.80003 13.8787 3.12503 13.638 2.47503C13.3973 1.82503 13.0513 1.23336 12.6 0.700025C12.8333 0.616692 13.0667 0.562359 13.3 0.537026C13.5333 0.511692 13.7667 0.499359 14 0.500026C15.1 0.500026 16.0417 0.891692 16.825 1.67503C17.6083 2.45836 18 3.40003 18 4.50003Z"
                                                fill="currentColor" />
                                        </svg>
                                        <span>{{ `(${answerRate(type)}%)`
                                        }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="grid grid-cols-4 gap-5 items-center p-3" v-for="student in studentAnswers">
                            <div class="col-span-2">{{ student.full_name }}</div>
                            <div class="">{{
                                utils.formatSecondsToMinuteSeconds(((getStudentCurentQuestion(student)?.endTime ?? 0) -
                                    (getStudentCurentQuestion(student)?.startTime ?? 0)), 'mmms') }}</div>
                            <div class="flex items-center gap-2" :class="evaluateAnswers(student).className">
                                <svg class="size-4" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="50" cy="50" r="50" fill="currentColor" />
                                </svg>
                                {{ evaluateAnswers(student).status }}
                            </div>
                        </div>
                    </div>
                </Slide>
            </div>
        </Flip>
        <div class="flex flex-col gap-3 sm:flex-row justify-between items-center w-full bg-white px-5 py-3 rounded-lg">
            <div class="hidden sm:flex md:flex-col min-w-28 gap-1 items-center font-bold">
                <template v-if="userStore.userRole === UserRole.teacher">
                    <p class="text-orange-500 dark:text-rose-600 first-letter:uppercase">{{
                        t(TEXT.view_submission_quiz_toggle) }}</p>
                    <label class="inline-flex items-center cursor-pointer">
                        <input type="checkbox" class="sr-only peer" v-model="showSubmission"
                            @change="audioManager.play(pageFlip, QuizAudioKey.SFX)">
                        <div
                            class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-purple-100 peer-focus:ring-4 rounded-full  peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-500 dark:peer-checked:dark:bg-purple-800">
                        </div>
                    </label>
                </template>
            </div>
            <QuestionNav :dark-mode="true" :current="currentQuestionIndex" :total="quizStore.quiz?.questions.length"
                @change="changeQuestion" />
            <div class="flex items-center justify-center gap-3">
                <ButtonV2 color="success" animation @click="$emit('back')">
                    {{ t(TEXT.view_ranking_btn) }}
                </ButtonV2>
            </div>
        </div>
    </div>
</template>
